/**
 * Per-player list selection sheet container.
 *
 * Manages local toggle state (diff from server), derives visual checked IDs,
 * and commits batch add/remove mutations on confirm.
 * Inline watchlist creation replaces the modal prompt — the new list
 * appears already checked and the input collapses on success.
 * Supports rename, delete, and share actions on user-owned lists.
 * @module features/scouting/components/SelectListSheet/SelectListSheetContainer
 */

import React, { useCallback, useMemo, useRef, useState } from "react";

import DeleteWatchlistDialog from "@/features/scouting/components/molecules/DeleteWatchlistDialog";
import RenameWatchlistModal from "@/features/scouting/components/molecules/RenameWatchlistModal";

import SelectListSheetView from "./SelectListSheetView";

import type { SelectListItem } from "./SelectListSheetView";

import MobileShareWatchlistSheet from "@/features/scouting/components/organisms/MobileShareWatchlistSheet";
import {
  UserWatchlistRole,
  usePlayerSearchAddPlayerToWatchlistMutation,
  usePlayerSearchCreateWatchlistMutation,
  usePlayerSearchRemovePlayerFromWatchlistMutation,
  useWatchlistDeleteWatchlistMutation,
  useWatchlistListMyUserWatchlistsQuery,
  useWatchlistUpdateWatchlistMutation,
} from "@/generated/graphql";
import { watchlistNameError } from "@/features/scouting/utils/watchlistNameRules";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import { useToastNotification } from "@/hooks/useToastNotification";
import { modifyWatchlistEntityPlayers } from "@/utils/handleWatchlistPlayers";

/** Operation name for the recent activity query — refetched after save */
const RECENT_ACTIVITY_QUERY = "ScoutingListRecentlyInteractedPlayers";

/** Operation name for the All Targets table query — refetched after save */
const ALL_TARGETS_QUERY = "ScoutingListAllTargets";

/** Target for rename/delete operations — stores the watchlist ID and name */
interface WatchlistTarget {
  readonly id: string;
  readonly name: string;
}

/** Props for SelectListSheetContainer */
interface SelectListSheetContainerProps {
  /** Whether the sheet is open */
  readonly isOpen: boolean;
  /** Callback to close the sheet */
  readonly onClose: () => void;
  /** Player ID to manage list membership for */
  readonly playerId: string;
  /** Player display name */
  readonly playerName: string;
  /** Optional callback when membership changes are saved */
  readonly onMembershipChange?: () => void;
}

/**
 * Container for the per-player list selection sheet.
 * Manages toggle state, batch mutations, inline watchlist creation,
 * and rename/delete/share actions on user-owned lists.
 * @param root0 - Container props
 * @param root0.isOpen - Whether sheet is visible
 * @param root0.onClose - Close handler
 * @param root0.playerId - Player ID
 * @param root0.playerName - Player display name
 * @param root0.onMembershipChange - Optional membership change callback
 */
const SelectListSheetContainer = ({
  isOpen,
  onClose,
  playerId,
  playerName,
  onMembershipChange,
}: SelectListSheetContainerProps) => {
  const toast = useToastNotification();
  const isCreatingRef = useRef(false);

  const { data: watchlistData, refetch: refetchWatchlists } =
    useWatchlistListMyUserWatchlistsQuery({
      variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    });

  const [addPlayerToWatchlist] = usePlayerSearchAddPlayerToWatchlistMutation();
  const [removePlayerFromWatchlist] =
    usePlayerSearchRemovePlayerFromWatchlistMutation();
  const [createWatchlist] = usePlayerSearchCreateWatchlistMutation({
    refetchQueries: [
      "WatchlistListMyUserWatchlists",
      "ScoutingListUserWatchlists",
    ],
    awaitRefetchQueries: true,
  });
  const [updateWatchlist] = useWatchlistUpdateWatchlistMutation();
  const [deleteWatchlist] = useWatchlistDeleteWatchlistMutation();

  /**
   * Local toggle state — only contains entries that differ from server state.
   * true = user wants to add, false = user wants to remove.
   */
  const [localToggles, setLocalToggles] = useState<
    Readonly<Record<string, boolean>>
  >({});
  const [isSaving, setIsSaving] = useState(false);

  /* --- Inline create state --- */
  const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  /* --- Rename/Delete/Share state --- */
  const [renameTarget, setRenameTarget] = useState<WatchlistTarget | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<WatchlistTarget | null>(
    null
  );
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  /**
   * SE-5437: share target for the modern MobileShareWatchlistSheet — the
   * sheet sources members/org-users itself via useShareWatchlist, replacing
   * the legacy Share organism + changeShareVar reactive-var flow (and its
   * collaborator query, which existed only to feed the legacy modal).
   */
  const [shareTarget, setShareTarget] = useState<WatchlistTarget | null>(null);

  /** Flat list of watchlist items with player counts (excludes default roster — SE-4298) */
  const items = useMemo((): readonly SelectListItem[] => {
    const edges = watchlistData?.listMyUserWatchlists?.edges ?? [];
    return edges
      .filter(edge => !edge.node.isDefault)
      .map(edge => ({
        id: edge.node.watchlist.id,
        name: edge.node.watchlist.name,
        playerCount: edge.node.watchlist.players?.edges?.length ?? 0,
        // PRD-6.2 (SE-8313): rename, delete and sharing are owner-only.
        canEdit: edge.node.role === UserWatchlistRole.Owner,
        canDelete: edge.node.role === UserWatchlistRole.Owner,
      }));
  }, [watchlistData]);

  /**
   * Every list name the client knows, default roster included (SE-8449): the
   * create form and the rename modal refuse a case-variant inline before the
   * round-trip. The backend still decides for names the client cannot see.
   */
  const existingNames = useMemo((): readonly string[] => {
    const edges = watchlistData?.listMyUserWatchlists?.edges ?? [];
    return edges.map(edge => edge.node.watchlist.name);
  }, [watchlistData]);

  // SE-8449: the shared rule, applied live. An untouched field shows no
  // message (Add is simply disabled); anything typed is judged as typed.
  const newListNameError =
    newListName.length > 0
      ? watchlistNameError(newListName, existingNames)
      : null;
  const isNewListNameValid =
    newListName.length > 0 && newListNameError === null;

  /** Server state — which watchlists already contain this player */
  const serverCheckedIds = useMemo((): readonly string[] => {
    const edges = watchlistData?.listMyUserWatchlists?.edges ?? [];
    return edges
      .filter(edge =>
        edge.node.watchlist.players?.edges?.some(
          playerEdge => playerEdge.node.id === playerId
        )
      )
      .map(edge => edge.node.watchlist.id);
  }, [watchlistData, playerId]);

  /** Visual checked state — server state + local toggles applied */
  const visualCheckedIds = useMemo((): readonly string[] => {
    return items
      .filter(
        item => localToggles[item.id] ?? serverCheckedIds.includes(item.id)
      )
      .map(item => item.id);
  }, [items, localToggles, serverCheckedIds]);

  /**
   * Toggles a watchlist in local state without firing mutations.
   * If the new value matches server state, removes the entry from localToggles.
   */
  const handleToggle = useCallback(
    (watchlistId: string) => {
      const isServerChecked = serverCheckedIds.includes(watchlistId);
      setLocalToggles(prev => {
        const currentVisual = prev[watchlistId] ?? isServerChecked;
        const newValue = !currentVisual;
        return newValue === isServerChecked
          ? Object.fromEntries(
              Object.entries(prev).filter(([key]) => key !== watchlistId)
            )
          : { ...prev, [watchlistId]: newValue };
      });
    },
    [serverCheckedIds]
  );

  /** Commits all local toggles to the server via parallel mutations */
  const handleConfirm = useCallback(async () => {
    const toAdd = Object.entries(localToggles)
      .filter(([, value]) => value)
      .map(([id]) => id);
    const toRemove = Object.entries(localToggles)
      .filter(([, value]) => !value)
      .map(([id]) => id);

    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      // SE-6777 - neither refetched query, nor the trailing
      // `refetchWatchlists()` (`WatchlistListMyUserWatchlists`), selects
      // `Watchlist.playerCount`. Writing the normalized entity settles both
      // count badges with no extra round trip.
      await Promise.all([
        ...toAdd.map(watchlistId =>
          addPlayerToWatchlist({
            variables: { input: { watchlistId, playerId } },
            refetchQueries: [RECENT_ACTIVITY_QUERY, ALL_TARGETS_QUERY],
            awaitRefetchQueries: true,
            update: (cache, { data, errors }) => {
              if (!data?.addPlayerToWatchlist || errors) return;
              modifyWatchlistEntityPlayers(cache, watchlistId, playerId, "add");
            },
          })
        ),
        ...toRemove.map(watchlistId =>
          removePlayerFromWatchlist({
            variables: { input: { watchlistId, playerId } },
            refetchQueries: [RECENT_ACTIVITY_QUERY, ALL_TARGETS_QUERY],
            awaitRefetchQueries: true,
            update: (cache, { data, errors }) => {
              if (!data?.removePlayerFromWatchlist || errors) return;
              modifyWatchlistEntityPlayers(
                cache,
                watchlistId,
                playerId,
                "remove"
              );
            },
          })
        ),
      ]);
      await refetchWatchlists();
      onMembershipChange?.();
      setLocalToggles({});
      toast.show({
        action: "success",
        description: `${playerName} watchlist changes saved`,
      });
      onClose();
    } catch (error) {
      console.warn("Failed to save watchlist changes:", error);
      toast.show({
        action: "danger",
        description: "Failed to save watchlist changes",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    localToggles,
    playerId,
    playerName,
    addPlayerToWatchlist,
    removePlayerFromWatchlist,
    refetchWatchlists,
    onMembershipChange,
    onClose,
    toast,
  ]);

  const handleCancel = useCallback(() => {
    setLocalToggles({});
    onClose();
  }, [onClose]);

  /** Creates a new watchlist inline, auto-checks it, and collapses the input */
  const handleCreateList = useCallback(async () => {
    const trimmed = newListName.trim();
    if (!isNewListNameValid || isCreatingRef.current) return;

    isCreatingRef.current = true;
    setIsCreating(true);
    try {
      const result = await createWatchlist({
        variables: { input: { name: trimmed } },
      });
      const createdId = result.data?.createWatchlist?.id;
      if (createdId) {
        setLocalToggles(prev => ({ ...prev, [createdId]: true }));
      }
      await refetchWatchlists();
      setNewListName("");
      setIsCreateFormVisible(false);
      toast.show({
        action: "success",
        description: `"${trimmed}" created`,
      });
    } catch (error) {
      console.warn("Failed to create watchlist:", error);
      toast.show({
        action: "danger",
        description: "Failed to create list",
      });
    } finally {
      isCreatingRef.current = false;
      setIsCreating(false);
    }
  }, [
    newListName,
    isNewListNameValid,
    createWatchlist,
    refetchWatchlists,
    toast,
  ]);

  const handleClearNewListName = useCallback(() => {
    setNewListName("");
    setIsCreateFormVisible(false);
  }, []);

  const handleToggleCreateForm = useCallback(() => {
    setIsCreateFormVisible(true);
  }, []);

  /* --- Rename handlers --- */
  const handleEditPress = useCallback(
    (watchlistId: string, watchlistName: string) => {
      setRenameTarget({ id: watchlistId, name: watchlistName });
    },
    []
  );

  const handleRenameSubmit = useCallback(
    async (newName: string) => {
      if (!renameTarget) return;
      setIsRenaming(true);
      try {
        await updateWatchlist({
          variables: { input: { id: renameTarget.id, name: newName } },
        });
        await refetchWatchlists();
        toast.show({
          action: "success",
          description: `List renamed to "${newName}"`,
        });
        setRenameTarget(null);
      } catch (error) {
        console.warn("Failed to rename watchlist:", error);
        toast.show({
          action: "danger",
          description: "Failed to rename list",
        });
      }
      setIsRenaming(false);
    },
    [renameTarget, updateWatchlist, refetchWatchlists, toast]
  );

  const handleRenameClose = useCallback(() => {
    setRenameTarget(null);
  }, []);

  /* --- Delete handlers --- */
  const handleDeletePress = useCallback(
    (watchlistId: string, watchlistName: string) => {
      setDeleteTarget({ id: watchlistId, name: watchlistName });
    },
    []
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteWatchlist({
        variables: { input: { id: deleteTarget.id } },
      });
      await refetchWatchlists();
      onMembershipChange?.();
      toast.show({
        action: "success",
        description: `"${deleteTarget.name}" deleted`,
      });
      setDeleteTarget(null);
    } catch (error) {
      console.warn("Failed to delete watchlist:", error);
      toast.show({
        action: "danger",
        description: "Failed to delete list",
      });
    }
    setIsDeleting(false);
  }, [
    deleteTarget,
    deleteWatchlist,
    refetchWatchlists,
    onMembershipChange,
    toast,
  ]);

  const handleDeleteClose = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  /* --- Share handler --- */
  const handleSharePress = useCallback((watchlistId: string, name: string) => {
    setShareTarget({ id: watchlistId, name });
  }, []);

  const handleShareClose = useCallback(() => {
    setShareTarget(null);
  }, []);

  /** Pre-built edit handlers keyed by watchlist ID */
  const editHandlers = useMemo(() => {
    return items
      .filter(item => item.canEdit)
      .reduce(
        (acc, item) => ({
          ...acc,
          [item.id]: () => handleEditPress(item.id, item.name),
        }),
        {} as Record<string, () => void>
      );
  }, [items, handleEditPress]);

  /** Pre-built delete handlers keyed by watchlist ID */
  const deleteHandlers = useMemo(() => {
    return items
      .filter(item => item.canDelete)
      .reduce(
        (acc, item) => ({
          ...acc,
          [item.id]: () => handleDeletePress(item.id, item.name),
        }),
        {} as Record<string, () => void>
      );
  }, [items, handleDeletePress]);

  /** Pre-built share handlers keyed by watchlist ID */
  const shareHandlers = useMemo(() => {
    return items
      .filter(item => item.canEdit)
      .reduce(
        (acc, item) => ({
          ...acc,
          [item.id]: () => handleSharePress(item.id, item.name),
        }),
        {} as Record<string, () => void>
      );
  }, [items, handleSharePress]);

  return (
    <>
      <SelectListSheetView
        isOpen={isOpen}
        onClose={onClose}
        playerName={playerName}
        items={items}
        checkedIds={visualCheckedIds}
        onToggle={handleToggle}
        isSaving={isSaving}
        onCreateList={handleCreateList}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        newListName={newListName}
        onNewListNameChange={setNewListName}
        newListNameError={newListNameError}
        isNewListNameValid={isNewListNameValid}
        onClearNewListName={handleClearNewListName}
        isCreating={isCreating}
        isCreateFormVisible={isCreateFormVisible}
        onShowCreateForm={handleToggleCreateForm}
        editHandlers={editHandlers}
        deleteHandlers={deleteHandlers}
        shareHandlers={shareHandlers}
      />
      <RenameWatchlistModal
        isOpen={renameTarget !== null}
        currentName={renameTarget?.name ?? ""}
        existingNames={existingNames}
        isRenaming={isRenaming}
        onClose={handleRenameClose}
        onSubmit={handleRenameSubmit}
      />
      <DeleteWatchlistDialog
        isOpen={deleteTarget !== null}
        watchlistName={deleteTarget?.name ?? ""}
        isDeleting={isDeleting}
        onClose={handleDeleteClose}
        onConfirmDelete={handleConfirmDelete}
      />
      <MobileShareWatchlistSheet
        isOpen={shareTarget !== null}
        watchlistId={shareTarget?.id ?? ""}
        listName={shareTarget?.name ?? ""}
        onClose={handleShareClose}
      />
    </>
  );
};

SelectListSheetContainer.displayName = "SelectListSheetContainer";

export default SelectListSheetContainer;
